import { MarketPairV2, WalletType } from "@cede/types";
import { API_ENDPOINT } from "@cede/utils";
import { isPerpsExchange } from "./config";
import { useApi } from "@/hooks/core/useApi";
import { useQuery } from "@tanstack/react-query";

interface UseCexPerpsMarketsOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch perpetual futures markets for a CEX exchange
 * Exchange-agnostic: pass any supported exchangeId
 * Returns MarketPairV2 which includes price data, volume, and leverage info
 */
export function useCexPerpsMarkets(exchangeId: string, options: UseCexPerpsMarketsOptions = {}) {
  const { enabled = true } = options;
  const apiHelper = useApi();

  return useQuery({
    queryKey: ["cex-perps-markets", exchangeId],
    queryFn: async (): Promise<MarketPairV2[]> => {
      // Validate exchange is supported
      if (!isPerpsExchange(exchangeId)) {
        return [];
      }

      // Request only USD_M_FUTURES markets from the API
      const markets: MarketPairV2[] = await apiHelper.getOrThrow(API_ENDPOINT.HUB_TRADE_MARKET_PAIRS_V2, {
        queryParams: {
          exchangeId,
          walletTypes: WalletType.USD_M_FUTURES,
        },
      });

      // Drop inverse contracts, which settle in the base (e.g. BTC/USD:BTC) and aren't supported by the trading widget.
      // Filtering on `settlementToken !== base` (rather than `=== quote`) keeps linear perps whose settle token differs
      // from the quote, e.g. Katana's BTC/USD:VBUSDC (single-collateral VBUSDC, quoted in USD).
      return markets.filter((market) => market.settlementToken !== market.base);
    },
    enabled: enabled && isPerpsExchange(exchangeId),
    staleTime: 30_000, // 30 seconds
    refetchInterval: 60_000, // 1 minute
  });
}
